import React, { useEffect, useState } from "react";
import {
    Table,
    Input,
    Button,
    Space,
    Popconfirm,
    Tooltip,
    Modal,
    Form,
    InputNumber,
    Switch,
    message,
    Alert,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { apiCall } from "../../../API/RestApi";
import { useSelector } from "react-redux";

const { Search } = Input;

export default function LeavePaidGenerate() {
    const token = useSelector((state) => state.user.token);
    const [data, setData] = useState([]);
    const [page, setPage] = useState({
        pagination: { current: 1, pageSize: 10 },
    });
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);

    // modal states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();
    const [editingRecord, setEditingRecord] = useState(null);

    useEffect(() => {
        getApi();
        // eslint-disable-next-line
    }, [JSON.stringify(page), search]);

    const getApi = () => {
        setLoading(true);
        const dataReq = {
            method: "GET",
            url: `/userleaveyear?search=${search}&page=${page.pagination.current}`,
            data: {
                headers: {
                    Authorization: "Bearer " + token,
                },
            },
        };
        apiCall(dataReq)
            .then((res) => {
                if (res.status === "success") {
                    setData(res.data);
                    setPage({
                        ...page,
                        pagination: {
                            ...page.pagination,
                            total: res.meta.total,
                        },
                    });
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    // ===== CRUD Handlers =====
    const handleAdd = () => {
        setEditingRecord(null);
        form.resetFields();
        setIsModalOpen(true);
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        form.setFieldsValue({
            leave_earned: record.leave_earned,
            length_of_service: record.length_of_service,
            description: record.description,
            is_generate: record.is_generate,
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        const dataReq = {
            method: "DELETE",
            url: `/userleaveyear/${id}`,
            data: {
                headers: { Authorization: "Bearer " + token },
            },
        };
        apiCall(dataReq)
            .then((res) => {
                if (res.status === "success") {
                    message.success("Deleted successfully");
                    getApi();
                }
            })
            .catch(() => message.error("Failed to delete"));
    };

    const handleSubmit = (values) => {
        const dataReq = {
            method: editingRecord ? "PUT" : "POST",
            url: editingRecord
                ? `/userleaveyear/${editingRecord.user_leave_year_id}`
                : `/userleaveyear`,
            data: {
                data: values,
                headers: { Authorization: "Bearer " + token },
            }

        };

        apiCall(dataReq)
            .then((res) => {
                if (res.status === "success") {
                    message.success(editingRecord ? "Updated" : "Created");
                    setIsModalOpen(false);
                    getApi();
                }
            })
            .catch(() =>
                message.error(editingRecord ? "Failed to update" : "Failed to create")
            );
    };

    // ===== Table Columns =====
    const columns = [
        {
            title: "No",
            render: (text, record, index) =>
                (page.pagination.current - 1) * page.pagination.pageSize +
                (index + 1),
        },
        {
            title: "Leave Earned",
            dataIndex: "leave_earned",
            key: "leave_earned",
            render: (val) => val + " Days",
        },
        {
            title: "Length Of Service",
            dataIndex: "length_of_service",
            key: "length_of_service",
            render: (val) => val + " Years",
        },
        { title: "Description", dataIndex: "description", key: "description" },
        {
            title: "Generated",
            dataIndex: "is_generate",
            render: (val) => (val ? "Yes" : "No"),
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button
                            type="primary"
                            onClick={() => handleEdit(record)}
                        >
                            <EditOutlined />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Popconfirm
                            title="Are you sure delete this record?"
                            onConfirm={() =>
                                handleDelete(record.user_leave_year_id)
                            }
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button type="primary" danger>
                                <DeleteOutlined />
                            </Button>
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Alert
                message="⚠️ Keterangan Penting"
                description="Hak cuti karyawan akan otomatis direset setiap bulan Januari. Pastikan cuti yang tersisa di tahun berjalan sudah digunakan sebelum reset dilakukan agar tidak hangus."
                type="warning"
                showIcon
                style={{ marginBottom: 15 }}
            />
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 10,
                }}
            >

                <Search
                    placeholder="Search"
                    allowClear
                    style={{ width: "40%" }}
                    enterButton="Search"
                    size="middle"
                    onSearch={(value) => {
                        setSearch(value);
                        setPage({ pagination: { current: 1, pageSize: 10 } });
                    }}
                />

                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                >
                    Add
                </Button>
            </div>

            <Table
                size="middle"
                columns={columns}
                dataSource={data}
                pagination={page.pagination}
                loading={loading}
                onChange={(pagination) => setPage({ pagination })}
                rowKey="user_leave_year_id"
            />

            {/* Modal Add/Edit */}
            <Modal
                title={editingRecord ? "Edit Leave Year" : "Add Leave Year"}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ is_generate: false }}
                >
                    <Form.Item
                        name="leave_earned"
                        label="Leave Earned (Days)"
                        rules={[
                            { required: true, message: "Please input leave earned" },
                        ]}
                    >
                        <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>

                    <Form.Item
                        name="length_of_service"
                        label="Length Of Service (Years)"
                        rules={[
                            {
                                required: true,
                                message: "Please input length of service",
                            },
                        ]}
                    >
                        <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>

                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item
                        name="is_generate"
                        label="Generated"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {editingRecord ? "Update" : "Create"}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
